import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createRef, useCallback, useEffect, useState } from "react";



type Props = {
    trigger?: () => void;
    onOKClicked?: (answer: number) => void;
    disabled: boolean;
}

export default function Keypad(props: Props) {
    const keypadDiv = createRef<HTMLDivElement>();
    const [ currentAnswer, setCurrentAnswer ] = useState("");
    const buttonClicked = useCallback((num: number) => {
        if (props.disabled)
            return;

        if (props.trigger)
            props.trigger();

        setCurrentAnswer((value) => { return `${value}${num}`});
    }, [ setCurrentAnswer, props.disabled ])

    const backspace = useCallback(() => {
        if (props.disabled)
            return;

        if (props.trigger)
            props.trigger();

        setCurrentAnswer((value) => value.slice(0, -1));
    }, [ setCurrentAnswer, props.disabled ])

    const okClicked = useCallback(() => {
        if (props.disabled || currentAnswer === "")
            return;

        if (props.trigger)
            props.trigger();

        if (props.onOKClicked)
            props.onOKClicked(parseInt(currentAnswer));

        setCurrentAnswer("");
    }, [ currentAnswer, props.disabled ])

    const keyPressed = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {

        const keyCode = event.code;

        if (keyCode.startsWith("Digit")) {
            buttonClicked(parseInt(keyCode.slice(5)));
            event.preventDefault();
        }
        else if (keyCode.startsWith("Numpad")) {
            buttonClicked(parseInt(keyCode.slice(6)));
            event.preventDefault();
        }
        else if (keyCode === "Backspace") {
            backspace();
            event.preventDefault();
        }
        else if (keyCode === "Enter") {
            okClicked();
            event.preventDefault();
        }
    }, [okClicked, backspace, buttonClicked])

    useEffect(() => {
        if (keypadDiv.current)
            keypadDiv.current.focus();
    }, [])

    const buttonStyle = `w-full h-16 sm:h-20 lg:h-30 bg-slate-200 items-center rounded-lg text-center flex flex-row select-none font-bold text-3xl sm:text-5xl md:text-7xl ${props.disabled ? "" : "hover:cursor-pointer"}`
    const okColors = props.disabled || currentAnswer === "" ? "bg-slate-100 text-slate-400" : "bg-green-300 text-foreground"

    return (
        <div className="flex flex-col w-full focus:ring-0 focus:border-none outline-none" ref={keypadDiv} tabIndex={-1} onKeyDown={(event) => { keyPressed(event)}}>
            <div>
                <span className="text-5xl md:text-7xl font-bold text-slate-700">{currentAnswer === "" ? `\u00A0` : currentAnswer}</span>
            </div>
            <div className="flex-grow flex flex-row gap-4 w-full">
                <div className="grid grid-cols-3 gap-2 md:gap-4 w-full justify-items-center">
                    {
                        [1,2,3,4,5,6,7,8,9].map((num) => {
                            return (
                                <div key={num} className={buttonStyle} onClick={() => { buttonClicked(num)}}><div className="justify-center flex-grow text-center">{num}</div></div>
                            )
                        })
                    }
                    <div className={`col-start-2 ${buttonStyle}`} onClick={() => buttonClicked(0)}><div className="justify-center flex-grow text-center">0</div></div>
                </div>
                <div className="flex flex-col gap-4">
                    <div className={`bg-slate-300 rounded-lg ${props.disabled ? "" : "hover:cursor-pointer"} flex flex-col justify-center md:w-40 md:h-20 w-12 sm:w-20 md:w-40 h-10`} onClick={() => { backspace() }}>
                        <div className="h-fit text-center text-3xl font-bold" >
                            <FontAwesomeIcon icon={faChevronLeft}/>
                        </div>
                    </div>
                    <div className={`flex-grow ${okColors} rounded-lg ${props.disabled || currentAnswer === "" ? "" : "hover:cursor-pointer"} flex flex-col justify-center md:w-40 w-12 sm:w-20 md:w-40`} onClick={() => { okClicked() }}>
                        <div className="h-fit text-center text-3xl font-bold" >OK</div>
                    </div>
                </div>
            </div>
        </div>
    )
}